import {
  CheckCircleFilled,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ToastService } from "@shared/lib/services";
import { IBriefLedger, LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import { Button, Input, Modal, Steps } from "@shared/ui";
import { useEffect } from "react";
import { useAddBrandAccountingState } from "./model/useAddBrandAccountingState";
import styles from "./styles.module.scss";
import { BillingSummaryForm } from "./ui/BillingSummaryForm";
import { BillingSummaryTable } from "./ui/BillingSummaryTable";
import { BrandSelectionTable } from "./ui/BrandSelectionTable";
import { CampaignSelectionTable } from "./ui/CampaignSelectionTable";
import { PurchaseSelectionTable } from "./ui/PurchaseSelectionTable";

interface AddBrandAccountingProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddBrandAccounting({
  isOpen,
  onClose,
}: AddBrandAccountingProps) {
  const state = useAddBrandAccountingState(onClose);

  const handleCopySummary = () => {
    const selectedPurchasesData = state.selectedPurchases;
    let tableText =
      "Creator Name\tCampaign\tPurchase Cost\tService Cost\tGST\tTotal\n";
    selectedPurchasesData.forEach((p) => {
      const isNegative = p.type === LEDGER_TYPE.Credit;
      tableText += `${p.socialMediaName || p?.category || ""}\t${p.brief?.name || ""}\t₹${isNegative ? -p.baseCost : p.baseCost}\t₹${isNegative ? -p.serviceCost : p.serviceCost}\t₹${isNegative ? -p.gstAmount : p.gstAmount}\t₹${isNegative ? -p.totalAmount : p.totalAmount}\n`;
    });
    const summary = `
Brand: ${state.selectedBrand}
Type: ${state.billingType}

${tableText}
--------------------------------------------------
Summary Totals:
Base Amount: ₹${state.totalServiceCost.toLocaleString()}
Service Cost : ₹${state.totalServiceCost.toLocaleString()}
GST: ₹${state.totalGst.toLocaleString()}
Grand Total: ₹${state.grandTotal.toLocaleString()}
    `.trim();
    navigator.clipboard.writeText(summary);
    ToastService.success("Summary table and details copied to clipboard!");
  };

  const handleSubmit = (e?: React.FormEvent) => {
    const result = state.handleSubmit(e);
    if (result?.error) {
      ToastService.error(result.error);
    }
  };

  const selectedPurchasesList = state.selectedPurchases;

  useEffect(() => {
    if (!isOpen) {
      state.reset();
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={720}
      closeIcon={false}
      destroyOnClose
      className={styles.modal}
      centered
      styles={{ body: { padding: 0 } }}
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div>
            <h2 className={styles.title}>Add New Billing</h2>
            <p className={styles.subtitle}>Internal Finance</p>
          </div>
          <div className={styles.stepDivider} />
          <div className={styles.stepIndicatorWrap}>
            <Steps
              current={state.step - 1}
              size="small"
              items={[
                { title: "Brand" },
                { title: "Campaigns" },
                { title: "Purchases" },
                { title: "Summary" },
              ]}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={styles.closeBtn}
          aria-label="Close"
        >
          <CloseOutlined />
        </button>
      </div>

      <div className={styles.body}>
        {state.showSuccess ? (
          <div className={styles.successState}>
            <div className={styles.successIconWrap}>
              <CheckCircleFilled className={styles.successIcon} />
            </div>
            <h3 className={styles.successTitle}>
              Billing Created Successfully
            </h3>
            <p className={styles.successText}>
              The billing entry has been added to the system.
            </p>
          </div>
        ) : (
          <>
            {state.step === 1 && (
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <label className={styles.stepLabel}>Select Brand</label>
                </div>
                <Input
                  placeholder="Search brands..."
                  value={state.brandSearch}
                  onChange={(e) => state.setBrandSearch(e.target.value)}
                  prefix={<SearchOutlined className={styles.searchIcon} />}
                  className={styles.searchInput}
                  allowClear
                />
                <BrandSelectionTable
                  dataSource={state.filteredBrands}
                  selectedBrand={state.selectedBrand}
                  onSelectBrand={state.setSelectedBrand}
                  isFetching={state.isFetchingBrands}
                />
              </div>
            )}

            {state.step === 2 && (
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <label className={styles.stepLabel}>Select Campaigns</label>
                  <span className={styles.count}>
                    {state.selectedCampaigns.length} selected
                  </span>
                </div>
                <Input
                  placeholder="Search campaigns..."
                  value={state.campaignSearch}
                  onChange={(e) => state.setCampaignSearch(e.target.value)}
                  className={styles.searchInput}
                  allowClear
                />
                <CampaignSelectionTable
                  isFetching={state.isFetchingBriefs}
                  dataSource={state.filteredCampaigns}
                  selectedCampaigns={state.selectedCampaigns}
                  onToggleCampaign={(id) => {
                    state.setSelectedCampaigns(
                      state.selectedCampaigns.includes(id)
                        ? state.selectedCampaigns.filter((x) => x !== id)
                        : [...state.selectedCampaigns, id]
                    );
                  }}
                />
              </div>
            )}

            {state.step === 3 && (
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <label className={styles.stepLabel}>Select Purchases</label>
                  <span className={styles.count}>
                    {state.selectedPurchases.length} selected
                  </span>
                </div>
                <Input
                  placeholder="Search purchases by description or ID..."
                  value={state.purchaseSearch}
                  onChange={(e) => state.setPurchaseSearch(e.target.value)}
                  className={styles.searchInput}
                  allowClear
                />
                <PurchaseSelectionTable
                  dataSource={state.filteredPurchases}
                  selectedPurchases={state.selectedPurchases.map((p) => p._id)}
                  onTogglePurchase={(briefLedgerEntry: IBriefLedger) => {
                    state.setSelectedPurchases(
                      state.selectedPurchases
                        .map((item) => item?._id)
                        .includes(briefLedgerEntry?._id)
                        ? state.selectedPurchases.filter(
                            (x) => x?._id !== briefLedgerEntry?._id
                          )
                        : [...state.selectedPurchases, briefLedgerEntry]
                    );
                  }}
                  onSelectAll={state.toggleSelectAllPurchases}
                  isAllSelected={
                    state.filteredPurchases.length > 0 &&
                    state.selectedPurchases.length ===
                      state.filteredPurchases.length
                  }
                  totalAmount={state.totalPurchaseCost}
                  totalSellingCost={state.totalSellingCost}
                  totalService={state.totalServiceCost}
                  totalGst={state.totalGst}
                  totalGrand={state.grandTotal}
                />
              </div>
            )}

            {state.step === 4 && (
              <div className={styles.stepContent}>
                <BillingSummaryTable
                  purchases={selectedPurchasesList}
                  totalPurchaseCost={state.totalPurchaseCost}
                  totalSellingCost={state.totalSellingCost}
                  totalServiceCost={state.totalServiceCost}
                  totalGst={state.totalGst}
                  grandTotal={state.grandTotal}
                  onCopySummary={handleCopySummary}
                />
                <BillingSummaryForm
                  billingType={state.billingType}
                  onBillingTypeChange={state.setBillingType}
                  invoiceNumber={state.invoiceNumber}
                  onInvoiceNumberChange={state.setInvoiceNumber}
                  invoiceDate={state.invoiceDate}
                  onInvoiceDateChange={state.setInvoiceDate}
                  invoiceAmount={state.invoiceAmount}
                  onInvoiceAmountChange={state.setInvoiceAmount}
                  uploadedFile={state.uploadedFile}
                  onFileChange={state.setUploadedFile}
                  onUploadSuccess={state.setUploadedFileUrl}
                  grandTotal={state.grandTotal}
                  amountError={
                    state.invoiceAmount !== "" &&
                    Number(state.invoiceAmount) !== state.grandTotal
                      ? `Amount must match grand total (₹${state.grandTotal.toLocaleString()})`
                      : undefined
                  }
                />
              </div>
            )}
          </>
        )}
      </div>

      {!state.showSuccess && (
        <div className={styles.footer}>
          <Button
            icon={<LeftOutlined />}
            onClick={state.handleBack}
            disabled={state.step === 1}
            className={styles.backBtn}
          >
            Back
          </Button>
          {state.step < 4 ? (
            <Button
              type="primary"
              icon={<RightOutlined />}
              onClick={state.handleNext}
              disabled={!state.canProceed}
              className={styles.nextBtn}
            >
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={state.isSubmitting}
              disabled={!state.isStep4Valid}
              className={styles.submitBtn}
            >
              {state.isSubmitting
                ? "Creating..."
                : `Create ${state.billingType}`}
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
}
